/**
 * Adaptive Bitrate (ABR) utility for Abidjan connectivity profiles.
 *
 * Abidjan network tiers (approximate):
 *   slow-2g / 2g  → 50–250 kbps  → serve 240p / lowest quality
 *   3g             → 250–750 kbps → serve 360p / medium quality
 *   4g (mobile)    → 750–5 Mbps   → serve 720p / high quality
 *   4g (desktop)   → 5+ Mbps      → serve 1080p / full quality
 *
 * Usage:
 *   import { getAdaptiveVideoSrc, getNetworkTier } from 'utils/adaptiveBitrate';
 *   const src = getAdaptiveVideoSrc(videoSources);
 */

// ── Network tier detection ────────────────────────────────────────────────────

/**
 * Returns one of: 'low' | 'medium' | 'high' | 'full'
 */
export const getNetworkTier = () => {
  const conn =
    navigator?.connection ||
    navigator?.mozConnection ||
    navigator?.webkitConnection;

  const isMobile =
    /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i?.test(
      navigator?.userAgent
    ) || window?.innerWidth <= 768;

  if (!conn) {
    // No Network Information API — conservative default for Abidjan mobile
    return isMobile ? 'medium' : 'high';
  }

  const { effectiveType, saveData, downlink } = conn;

  // Respect user's data-saver preference
  if (saveData) return 'low';

  switch (effectiveType) {
    case 'slow-2g': case'2g':
      return 'low';
    case '3g':
      return isMobile ? 'low' : 'medium';
    case '4g':
      if (downlink && downlink < 2) return 'medium';
      return isMobile ? 'high' : 'full';
    default:
      return isMobile ? 'medium' : 'high';
  }
};

// ── Quality label map ─────────────────────────────────────────────────────────

export const TIER_LABELS = {
  low: '240p',
  medium: '360p',
  high: '720p',
  full: '1080p',
};

// ── Source selector ───────────────────────────────────────────────────────────

/**
 * Given a sources object with optional quality keys, returns the best URL
 * for the current network tier.
 *
 * @param {Object|string} sources
 *   - string: single URL, returned as-is
 *   - object: { low, medium, high, full, default } — keys are optional
 * @returns {{ url: string, tier: string, label: string }}
 */
export const getAdaptiveVideoSrc = (sources) => {
  const tier = getNetworkTier();

  if (!sources) return { url: null, tier, label: TIER_LABELS?.[tier] };

  // Plain string URL — no ABR possible, return as-is
  if (typeof sources === 'string') {
    return { url: sources, tier, label: TIER_LABELS?.[tier] };
  }

  // Priority order: exact tier → fallback tiers → default
  const tierOrder = {
    low: ['low', 'medium', 'high', 'full', 'default'],
    medium: ['medium', 'low', 'high', 'full', 'default'],
    high: ['high', 'medium', 'full', 'low', 'default'],
    full: ['full', 'high', 'medium', 'low', 'default'],
  };

  const order = tierOrder?.[tier] || tierOrder?.['medium'];
  for (const key of order) {
    if (sources?.[key]) {
      return { url: sources?.[key], tier, label: TIER_LABELS?.[tier] };
    }
  }

  // Last resort: first truthy value in the object
  const fallback = Object.values(sources)?.find(Boolean);
  return { url: fallback || null, tier, label: TIER_LABELS?.[tier] };
};

// ── React hook ────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';

/**
 * Hook that returns the adaptive video source and updates if the connection
 * changes (e.g. user moves from WiFi to 4G).
 *
 * @param {Object|string} sources
 * @returns {{ url: string|null, tier: string, label: string }}
 */
export const useAdaptiveVideo = (sources) => {
  const [result, setResult] = useState(() => getAdaptiveVideoSrc(sources));

  useEffect(() => {
    setResult(getAdaptiveVideoSrc(sources));

    const conn =
      navigator?.connection ||
      navigator?.mozConnection ||
      navigator?.webkitConnection;

    if (!conn) return;

    const handleChange = () => setResult(getAdaptiveVideoSrc(sources));
    conn?.addEventListener('change', handleChange);
    return () => conn?.removeEventListener('change', handleChange);
  }, [sources]);

  return result;
};

// ── Preload hint helper ───────────────────────────────────────────────────────

/**
 * Injects a <link rel="preload"> for the adaptive video source into <head>.
 * Call this early (e.g. on hover / focus) to warm up the connection.
 *
 * @param {string} url
 */
export const preloadVideoHint = (url) => {
  if (!url || typeof document === 'undefined') return;
  const existing = document.querySelector(`link[rel="preload"][href="${url}"]`);
  if (existing) return;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'video';
  link.href = url;
  document.head?.appendChild(link);
};
